/**
 * Entrance reveals.
 *
 * IntersectionObserver rather than a scroll handler or another ScrollTrigger
 * instance: the browser does the work off the main thread, and elements that
 * are never scrolled to cost nothing at all.
 *
 * Reveals fire once and unobserve. Content that fades out again when you scroll
 * back up is a common flourish and a bad one — it punishes re-reading, and
 * re-reading is what someone deciding whether to spend money actually does.
 *
 * Everything starts visible in CSS and is *hidden* by a class this module adds
 * on boot. If the script fails to load or JS is off, the page is simply fully
 * readable, which is the correct failure mode for a page whose job is to be
 * read.
 */

const HIDDEN = 'is-hidden';
const SHOWN = 'is-shown';

export function initReveals({ selector = '.reveal', stagger = 60 } = {}) {
  const nodes = Array.from(document.querySelectorAll(selector));
  if (!nodes.length) return;

  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  if (reduced || !('IntersectionObserver' in window)) {
    // Nothing to animate; leave the document in its plain readable state.
    return;
  }

  // Anything already on screen at boot is never hidden.
  //
  // This is not a nicety. Largest Contentful Paint is measured when the largest
  // element is actually *painted*, and starting the hero headline at opacity 0
  // pushes that moment out until script has run and a transition has finished.
  // Measured on this page: LCP 3.0s with a render delay of 2.5s, entirely
  // self-inflicted by the entrance animation. Content above the fold appears
  // immediately; only content the reader has to scroll to gets animated in.
  const fold = window.innerHeight;
  const deferred = [];
  for (const el of nodes) {
    if (el.getBoundingClientRect().top < fold * 0.9) continue;
    el.classList.add(HIDDEN);
    deferred.push(el);
  }
  if (!deferred.length) return;

  const observer = new IntersectionObserver(
    (entries) => {
      // Stagger by position within this batch, not by index in the document,
      // so a section scrolled past quickly does not queue up a long cascade.
      const visible = entries.filter((e) => e.isIntersecting);
      visible.forEach((entry, i) => {
        const el = entry.target;
        el.style.transitionDelay = `${Math.min(i * stagger, 240)}ms`;
        el.classList.remove(HIDDEN);
        el.classList.add(SHOWN);
        observer.unobserve(el);
      });
    },
    // Fire a little before the element reaches the fold so it is already
    // settled by the time it is properly in view.
    { rootMargin: '0px 0px -12% 0px', threshold: 0.15 },
  );

  for (const el of deferred) observer.observe(el);

  return () => observer.disconnect();
}